import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Loader2, CalendarDays } from "lucide-react";
import { useAuth } from "@/components/AuthContext";
import { useBookings } from "@/hooks/useBookings";
import { OfferFilter, offerKey } from "@/components/admin/OfferFilter";
import { ReservationsTable } from "@/components/admin/ReservationsTable";

const plural = (n, word) => `${n} ${word}${n > 1 ? "s" : ""}`;

const subtitle = (bookings, visible, offerFilter) => {
  if (!bookings) return "Chargement…";
  const base = `${bookings.length} rendez-vous`;
  return offerFilter ? `${base} · ${plural(visible.length, "affiché")}` : base;
};

const EmptyState = ({ filtered }) => (
  <div className="mt-16 rounded-2xl bg-argia-stone p-14 text-center" data-testid="reservations-empty">
    <CalendarDays size={40} strokeWidth={1.5} className="mx-auto text-argia-muted" />
    <p className="mt-4 text-base text-argia-muted">{filtered ? "Aucune réservation pour cette offre." : "Aucune réservation pour le moment."}</p>
  </div>
);

export default function Reservations() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { bookings, setStatus, remove } = useBookings(user);
  const [offerFilter, setOfferFilter] = useState(null);

  if (user === false) return <Navigate to="/admin/login" replace />;

  const visible = bookings && (offerFilter ? bookings.filter((b) => offerKey(b) === offerFilter) : bookings);

  const renderContent = () => {
    if (!bookings) return <div className="mt-16 flex justify-center text-argia-muted"><Loader2 className="animate-spin" /></div>;
    if (visible.length === 0) return <EmptyState filtered={Boolean(offerFilter)} />;
    return <ReservationsTable bookings={visible} onStatus={setStatus} onRemove={remove} />;
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10" data-testid="admin-reservations-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl">Réservations</h1>
          <p className="mt-2 text-sm text-argia-muted">{subtitle(bookings, visible, offerFilter)}</p>
        </div>
        <button
          data-testid="admin-logout"
          onClick={async () => { await logout(); navigate("/admin/login"); }}
          className="inline-flex items-center gap-2 rounded-full border border-argia-ink px-5 py-2.5 text-sm font-semibold text-argia-ink transition-colors duration-300 hover:bg-argia-ink hover:text-white"
        >
          <LogOut size={16} strokeWidth={1.5} /> Déconnexion
        </button>
      </div>

      {bookings && <OfferFilter bookings={bookings} selected={offerFilter} onChange={setOfferFilter} />}
      {renderContent()}
    </div>
  );
}
